import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { resolveEffectiveBookFromQuery } from '../../shared/utils/book-resolve.utils';
import { joinPathSearchAndHash } from '../../shared/utils/filter-redirect-url.utils';
import { deriveFilePathFromSiteLocation } from './route-change-path.utils';
import { BookActionName, useBookAction, useBookPath, useBookSerializeToUrl } from '../../state/BookContext';
import { FileActionName, useFileAction, useFileContext } from '../../state/FileContext';
import { FilterActionName, useFilterAction, useFilterContext } from '../../state/FilterContext';
import { useSiteContext } from '../../state/SiteContext';
import { FilterState } from '../../state/state-models';
import { applyDefaults, getStateAsLocationQuery, updateStateFromSearchParams } from '../../state/state-url-management';
import { isSetPasswordPath } from '../../shared/utils/set-password-path';

export function getNormalizedLayoutFromPathname(pathname: string, sitePath: string | null | undefined): string {
    const trimmedSitePath = (sitePath || "").replace(/\/+$/, "");
    const withoutSitePath = trimmedSitePath && pathname.startsWith(trimmedSitePath)
        ? pathname.substring(trimmedSitePath.length)
        : pathname;
    const [firstSegment] = withoutSitePath.split("/").filter(Boolean);
    if (!firstSegment?.startsWith("~")) {
        return "";
    }
    return firstSegment.substring(1).trim().toLowerCase();
}

/**
 * First completed filter→URL opportunity after mount uses history.replace
 * (SSR applyDefaults canonicalization). Later in-place writes push.
 * Call only when the write runs or the query is already canonical — not when deferred.
 */
export function consumePendingInitialFilterQueryReplace(
    pendingInitialCanonicalization: { current: boolean },
): boolean {
    const replace = pendingInitialCanonicalization.current;
    pendingInitialCanonicalization.current = false;
    return replace;
}

/***
 * This component has three main responsibilities:
 * 1. Update the file state and filter statewhen the location changes
 * 2. Update the URL query (keeping the current hash) when the filter state changes
 */
export const RouteChangeListener = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const updatePath = useFileAction(FileActionName.UPDATE_PATH);
    const updateBookPath = useBookAction(BookActionName.UPDATE_PATH);
    const bookPath = useBookPath();
    const bookSerializeToUrl = useBookSerializeToUrl();
    const setFilterState = useFilterAction(FilterActionName.SET_STATE);
    const { siteState } = useSiteContext();
    const { fileState } = useFileContext();

    const { filterState } = useFilterContext();

    const [synchronizationRequired, setSynchronizationRequired] = useState<boolean>(false);
    const filterStateChangedRecently = useRef(false);
    const internalFilterStateChangeIsActive = useRef(false);
    const searchParamsChangedRecently = useRef(false);
    const internalSearchParamsChangeIsActive = useRef(false);
    const lastProcessedSearchParams = useRef<string>("");
    const lastSyncedPathnameRef = useRef(location.pathname);
    const lastLayoutRef = useRef(getNormalizedLayoutFromPathname(location.pathname, siteState.sitePath));
    const pendingInitialUrlCanonicalization = useRef(true);

    useEffect(() => {
        const currentLayout = getNormalizedLayoutFromPathname(location.pathname, siteState.sitePath);
        if (currentLayout === lastLayoutRef.current) {
            return;
        }
        lastLayoutRef.current = currentLayout;
        window.location.replace(`${location.pathname}${location.search}${location.hash}`);
    }, [location.pathname, location.search, location.hash, siteState.sitePath]);

    useEffect(() => {
        if (location.pathname !== lastSyncedPathnameRef.current) {
            lastSyncedPathnameRef.current = location.pathname;
            // After redirect/navigation, search string may match the previous route; reset so URL→state runs for this page.
            lastProcessedSearchParams.current = "";
            searchParamsChangedRecently.current = true;
            setSynchronizationRequired(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!internalFilterStateChangeIsActive.current) {
            filterStateChangedRecently.current = true;
            setSynchronizationRequired(true);
        }
    }, [filterState]);

    useEffect(() => {
        if (!internalSearchParamsChangeIsActive.current) {
            searchParamsChangedRecently.current = true;
            setSynchronizationRequired(true);
        }
    }, [searchParams]);

    const memoizedPath = useMemo(
        () =>
            deriveFilePathFromSiteLocation({
                pathname: location.pathname,
                sitePath: siteState.sitePath,
                site: siteState.site ?? undefined,
                currentFilePath: fileState.path,
            }),
        [location.pathname, siteState.sitePath, siteState.site, fileState.path],
    );

    /***
 * Update the file state when the memoized path changes
 */
    useEffect(() => {
        updatePath(memoizedPath);
    }, [memoizedPath, updatePath]);


    useEffect(() => {
        if (synchronizationRequired) {
            // Filter sync drops unknown keys such as token. Leave this document's query alone.
            const sitePath = siteState.sitePath || "";
            const relativePath = sitePath && location.pathname.startsWith(sitePath)
                ? location.pathname.slice(sitePath.length) || "/"
                : location.pathname;
            if (isSetPasswordPath(relativePath) || isSetPasswordPath(memoizedPath)) {
                filterStateChangedRecently.current = false;
                searchParamsChangedRecently.current = false;
                setSynchronizationRequired(false);
                return;
            }
            const currentSearchParamsString = searchParams.toString();

            const trySyncFilterStateToUrl = (): void => {
                if (!filterStateChangedRecently.current) {
                    return;
                }
                // Defer until URL ingestion has updated lastProcessedSearchParams; otherwise we can drop a
                // filter→URL sync when searchParamsChangedRecently is true and current !== lastProcessed.
                const mayWriteFilterToUrl =
                    !searchParamsChangedRecently.current
                    || currentSearchParamsString === lastProcessedSearchParams.current;
                if (!mayWriteFilterToUrl) {
                    return;
                }
                const expectedSearchParams: URLSearchParams = getStateAsLocationQuery(filterState);
                if (bookPath && bookSerializeToUrl) {
                    expectedSearchParams.set("book", bookPath);
                }
                const expectedParamsString = expectedSearchParams.toString();
                const replace = consumePendingInitialFilterQueryReplace(pendingInitialUrlCanonicalization);
                if (currentSearchParamsString !== expectedParamsString) {
                    navigate(
                        joinPathSearchAndHash(location.pathname, expectedParamsString, location.hash),
                        { replace, preventScrollReset: true },
                    );
                    internalSearchParamsChangeIsActive.current = true;
                    setTimeout(() => {
                        internalSearchParamsChangeIsActive.current = false;
                    }, 0);
                }
                filterStateChangedRecently.current = false;
            };

            if (filterStateChangedRecently.current) {
                trySyncFilterStateToUrl();
            }

            let urlCausedFilterStateReplace = false;
            if (searchParamsChangedRecently.current) {
                // Only process if this is a new URL change (not the same as last processed)
                if (currentSearchParamsString !== lastProcessedSearchParams.current) {
                    const requestedBook = searchParams.get("book") || "";
                    const resolved = resolveEffectiveBookFromQuery(requestedBook, siteState.site ?? undefined);
                    updateBookPath({
                        bookPath: resolved.effectiveBookPath || undefined,
                        bookSerializeToUrl: resolved.bookSerializeToUrl,
                    });
                    const newFilterState: FilterState = updateStateFromSearchParams(searchParams, {});
                    if (siteState.site) {
                        applyDefaults(newFilterState, siteState.site);
                    }
                    // Check if the new filter state (derived from URL) would serialize back to the same URL
                    // This ensures we're comparing apples to apples - what matters is the URL representation
                    const newStateParams = getStateAsLocationQuery(newFilterState);
                    if (searchParams.get("book")) {
                        newStateParams.set("book", searchParams.get("book") || "");
                    }
                    const newStateParamsString = newStateParams.toString();

                    // Also check what the current filter state would serialize to
                    const currentStateParams = getStateAsLocationQuery(filterState);
                    const currentStateParamsString = currentStateParams.toString();

                    // Only update filter state if:
                    // 1. The new state would serialize to match the URL (it should, since we derived it from URL)
                    // 2. AND the current filter state would serialize to something different than the URL
                    // This means the filter state is out of sync with the URL and needs updating
                    if (newStateParamsString === currentSearchParamsString &&
                        currentStateParamsString !== currentSearchParamsString) {
                        // The new state matches the URL, but current state doesn't - update it
                        setFilterState(newFilterState);
                        urlCausedFilterStateReplace = true;
                        internalFilterStateChangeIsActive.current = true;
                        setTimeout(() => {
                            internalFilterStateChangeIsActive.current = false;
                        }, 0);
                    }
                    lastProcessedSearchParams.current = currentSearchParamsString;
                }
                searchParamsChangedRecently.current = false;
            }

            // Retry filter→URL after URL branch may have advanced lastProcessedSearchParams (same tick, fresh enough).
            // Do not run with stale filterState after setFilterState above; a follow-up sync is scheduled instead.
            if (filterStateChangedRecently.current && !urlCausedFilterStateReplace) {
                trySyncFilterStateToUrl();
            }

            if (filterStateChangedRecently.current) {
                // Must flip false → true so the effect runs again when sync was already true (deferred filter→URL).
                setSynchronizationRequired(false);
                queueMicrotask(() => {
                    setSynchronizationRequired(true);
                });
            } else {
                setSynchronizationRequired(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookPath, bookSerializeToUrl, location.hash, location.pathname, navigate, searchParams, setFilterState, siteState.site, filterState, synchronizationRequired, updateBookPath]);

    return null; // doesn't render anything
};